import React, { useMemo, useState } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { DepotStatusFilter } from "./depot-status-filter";
import { DepotTable } from "./depot-table";
import { useDepotSettings } from "../../model/useDepotSettings";
import { DepotDialog } from "../../../../features/depot-dialog/ui/depot-dialog";
import { useUpdateLocationMutation } from "~/shared/api/locations/locations.grpc";
import { ConnectProto } from "@racoon/api";
import { useToast } from "@racoon/ui";

interface DepotSettingsProps {
  selectedCompanyId?: number;
}

export const DepotSettings: React.FC<DepotSettingsProps> = ({ selectedCompanyId }) => {
  const t = useT();
  const { toast } = useToast();
  const {
    rows,
    counts,
    locationTab,
    setLocationTab,
    search,
    setSearch,
    isLoading,
    activeOperatorIds,
    toggleOperator,
    isActivating,
  } = useDepotSettings(selectedCompanyId);
  const isGlobal = locationTab === "global";

  const [updateDepot] = useUpdateLocationMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDepot, setSelectedDepot] = useState<ConnectProto.Location | null>(null);
  const [editingDepot, setEditingDepot] = useState<ConnectProto.Location | null>(null);

  const handleCreateClick = () => {
    setEditingDepot(null);
    setDialogOpen(true);
  };

  const handleDepotEdit = (depot: ConnectProto.Location) => {
    setEditingDepot(depot);
    setDialogOpen(true);
  };

  const handleRowSelect = (depot: ConnectProto.Location | null) => {
    setSelectedDepot(depot);
  };

  const handleStatusChange = async (
    status: ConnectProto.LocationStatus,
    successMessageKey: string,
  ) => {
    if (!selectedDepot) return;

    try {
      await updateDepot({
        id: BigInt(selectedDepot.id),
        ownerOrganizationId: BigInt(selectedCompanyId!),
        name: selectedDepot.name,
        address: selectedDepot.address,
        lat: selectedDepot.lat,
        lon: selectedDepot.lon,
        gisLink: selectedDepot.gisLink,
        serviceTime:
          selectedDepot.serviceTime != null ? BigInt(selectedDepot.serviceTime) : undefined,
        type: selectedDepot.type,
        status,
      }).unwrap();

      toast({
        title: t(Namespace.Settings, successMessageKey as any),
      });
      setSelectedDepot(null);
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "depot.dialog.error"),
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleArchiveClick = () =>
    handleStatusChange(ConnectProto.LocationStatus.INACTIVE, "depot.archived");

  const handleRestoreClick = () =>
    handleStatusChange(ConnectProto.LocationStatus.ACTIVE, "depot.restored");

  const isActive = selectedDepot?.status === ConnectProto.LocationStatus.ACTIVE;
  const isInactive = selectedDepot?.status === ConnectProto.LocationStatus.INACTIVE;

  const handleTabChange = (tab: typeof locationTab) => {
    setSelectedDepot(null);
    setLocationTab(tab);
  };

  // Stable while nothing it shows changes, so DepotTable keeps its memoized columns.
  const activation = useMemo(
    () =>
      isGlobal
        ? { activeOperatorIds, onToggle: toggleOperator, disabled: isActivating }
        : undefined,
    [isGlobal, activeOperatorIds, toggleOperator, isActivating],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <DepotStatusFilter
        activeTab={locationTab}
        onTabChange={handleTabChange}
        counts={counts}
        searchValue={search}
        onSearchChange={setSearch}
        onCreateClick={isGlobal ? undefined : handleCreateClick}
        onArchiveClick={isActive ? handleArchiveClick : undefined}
        onRestoreClick={isInactive ? handleRestoreClick : undefined}
      />
      <DepotTable
        depots={rows}
        isLoading={isLoading}
        selectedDepot={selectedDepot}
        onRowSelect={isGlobal ? undefined : handleRowSelect}
        onDepotEdit={isGlobal ? undefined : handleDepotEdit}
        activation={activation}
      />

      <DepotDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        depot={editingDepot}
        selectedCompanyId={selectedCompanyId}
      />
    </div>
  );
};
